import { useCallback, useEffect, useRef, useState } from 'react';
import { getDiagnosticBookingDetail, getMedicineBookingDetail } from '../services/subscriberWalletService';

// One popup, two modules. The subscriber portal opens a medicine/diagnostics booking's OWN
// detail view without leaving the subscriber page, so this hook owns exactly what the module
// booking pages own for their inline modal: which booking is open, its payload, and the
// load/error state. Nothing here is logged — the payload carries amounts, cashback and a
// mobile number.
export const MODULE_KIND_MEDICINE = 'medicine';
export const MODULE_KIND_DIAGNOSTICS = 'diagnostics';

const FETCHERS = {
  [MODULE_KIND_MEDICINE]: getMedicineBookingDetail,
  [MODULE_KIND_DIAGNOSTICS]: getDiagnosticBookingDetail,
};

export const useModuleBookingDetail = (currentRole, onUnavailable) => {
  const [kind, setKind] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // A slow response for a booking the user has already closed (or switched away from) must
  // not paint over the current one.
  const requestRef = useRef(0);

  // Held in a ref so a host that rebuilds the callback each render does not rebuild `load`
  // (and with it `retry`, whose identity the popups depend on).
  const onUnavailableRef = useRef(onUnavailable);
  useEffect(() => { onUnavailableRef.current = onUnavailable; }, [onUnavailable]);

  const load = useCallback(async (nextKind, nextBookingId) => {
    const fetcher = FETCHERS[nextKind];
    if (!fetcher || !nextBookingId || !currentRole) return;

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    setKind(nextKind);
    setBookingId(nextBookingId);
    setDetail(null);
    setError(null);
    setLoading(true);

    try {
      const response = await fetcher(currentRole, nextBookingId);
      if (requestRef.current !== requestId) return;
      // Both module detail endpoints answer under `msg`; `data` is the older shape the
      // module pages still tolerate.
      const payload = response?.msg || response?.data;
      if (payload) {
        setDetail(payload);
      } else {
        setError('Failed to load booking details');
      }
    } catch (err) {
      if (requestRef.current !== requestId) return;
      // A 4xx here is the server ANSWERING that this id is not one of its bookings (the
      // refund/ledger row case above): close this popup and let the host show the row's
      // generic wallet detail. A network failure or timeout carries no status — that stays
      // on the module endpoint with Retry, so a blip never silently downgrades a real
      // booking to the generic view.
      const status = err?.status;
      const notAModuleBooking = typeof status === 'number' && status >= 400 && status < 500;
      if (notAModuleBooking && onUnavailableRef.current) {
        setKind(null);
        setBookingId(null);
        onUnavailableRef.current(nextKind, nextBookingId);
        return;
      }
      setError('Failed to load booking details');
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [currentRole]);

  const open = useCallback((nextKind, nextBookingId) => {
    load(nextKind, nextBookingId);
  }, [load]);

  const retry = useCallback(() => {
    if (kind && bookingId) load(kind, bookingId);
  }, [kind, bookingId, load]);

  const close = useCallback(() => {
    requestRef.current += 1; // abandon any in-flight response
    setKind(null);
    setBookingId(null);
    setDetail(null);
    setError(null);
    setLoading(false);
  }, []);

  return { kind, bookingId, detail, loading, error, open, retry, close };
};

export default useModuleBookingDetail;
